<ng-container *ngIf="costAccountingState$ | async as state">
    <h1 mat-dialog-title class="active">
        <ng-container *ngIf="costAccounting$ | async; else displayHeader">
            {{ (costAccounting$ | async).name }}
        </ng-container>
        <ng-template #displayHeader>
            {{ "app.page.cost-accounting.actions.add.label" | translate }}
        </ng-template>
    </h1>
    <mat-progress-bar color="accent" [style.visibility]="state.isLoading ? 'visible' : 'hidden'" mode="indeterminate"></mat-progress-bar>
    <div mat-dialog-content>

        <mat-stepper #stepper
                     [selectedIndex]="activeStep">

            <mat-step
                [stepControl]="stepValidationFormGroup.controls['initializeStepControl']">
                <ng-template matStepLabel>{{ "app.dialog.cost-accounting.initialize.label" | translate }}</ng-template>
                <ng-template matStepContent>

                    <div class="dialogSection">

                        <mat-form-field appearance="fill">
                            <mat-label>{{ "app.dialog.cost-accounting.date-range.label" | translate }}</mat-label>
                            <mat-date-range-input [formGroup]="dialogFormGroup.controls.dateRange" [rangePicker]="picker">
                                <input matStartDate formControlName="start">
                                <input matEndDate formControlName="end">
                            </mat-date-range-input>
                            <mat-datepicker-toggle matSuffix [for]="picker"></mat-datepicker-toggle>
                            <mat-date-range-picker #picker></mat-date-range-picker>
                        </mat-form-field>

                        <mat-form-field appearance="fill">
                            <mat-label>{{ "app.dialog.cost-accounting.type.label" | translate }}</mat-label>
                            <mat-select [formControl]="dialogFormGroup.controls.costAccountingType">
                                <mat-option *ngFor="let type of state.costAccountingTypes" [value]="type.id">
                                    {{ type.name }}
                                </mat-option>
                            </mat-select>

                            <button matSuffix
                                    (click)="$event.stopPropagation()"
                                    [disabled]="selectedType === undefined || selectedType === null"
                                    mat-icon-button
                                    [matMenuTriggerFor]="dataSourcesInfoOverlay">
                                <mat-icon>info_outline</mat-icon>
                            </button>

                        </mat-form-field>

                    </div>

                    <mat-form-field appearance="fill">
                        <mat-label>{{ "app.dialog.cost-accounting.indirect-cost-date-range.label" | translate }}</mat-label>
                        <mat-date-range-input [formGroup]="dialogFormGroup.controls.indirectCostDateRange" [rangePicker]="picker2">
                            <input matStartDate formControlName="start">
                            <input matEndDate formControlName="end">
                        </mat-date-range-input>
                        <mat-datepicker-toggle matSuffix [for]="picker2"></mat-datepicker-toggle>
                        <mat-date-range-picker #picker2></mat-date-range-picker>
                        <button mat-icon-button matSuffix (click)="clearIndirectCostDateRange()"
                                [disabled]="(costAccounting$ | async) !== undefined">
                            <mat-icon>close</mat-icon>
                        </button>
                    </mat-form-field>

                    <mat-divider class="dialogDivider"></mat-divider>

                    <div class="dialogSection">
                        <mat-menu #dataSourcesInfoOverlay class="infoOverlay">
                            <!-- Display all parameters of the selected CostAccountingType -->
                            <ng-container *ngFor="let item of selectedType | keyvalue">
                                <div *ngIf="item.key !== 'id' && item.key !== 'name'" disableRipple mat-menu-item>
                                    <span class="parameterName">{{ "app.model.cost-accounting-type." + item.key + ".label" | translate }}: </span>
                                    <span class="parameterValue">{{ "app.model.cost-accounting-type." + item.key + "." + item.value + ".label" | translate }}</span>
                                </div>
                            </ng-container>
                        </mat-menu>

                        <mat-form-field appearance="fill">
                            <mat-label>{{ "app.dialog.cost-accounting.name.label" | translate }}</mat-label>
                            <input [formControl]="dialogFormGroup.controls.name" matInput>
                        </mat-form-field>
                    </div>

                    <div mat-dialog-actions>
                        <button mat-stroked-button mat-dialog-close (click)="close()">{{ "app.button.cancel.label" | translate }}</button>
                        <button mat-flat-button
                                color="primary"
                                [disabled]="!dialogFormGroup.valid || (costAccounting$ | async) !== undefined || state.isLoading"
                                (click)="initializeCostAccounting()">
                            {{ "app.dialog.cost-accounting.initialize.confirm.label" | translate }}
                        </button>
                    </div>
                </ng-template>
            </mat-step>
            <mat-step [stepControl]="stepValidationFormGroup.controls['costTypeAccountingStepControl']">
                <ng-template matStepLabel>{{ "app.dialog.cost-accounting.cost-type-accounting.label" | translate }}</ng-template>
                <ng-template matStepContent>

                    <mat-radio-group
                        id="radio-selection"
                        *ngIf="(costAccounting$ | async)?.state === stateEnum.INITIALIZED"
                        [(ngModel)]="indirectCostOption">
                        <mat-radio-button value="financial-accounting">{{ "app.dialog.cost-accounting.indirect-cost.financial-accounting.label" | translate }}</mat-radio-button>
                        <mat-radio-button value="manual">{{ "app.dialog.cost-accounting.indirect-cost.manual.label" | translate }}</mat-radio-button>
                    </mat-radio-group>
                    <div class="embeddedList">
                        <ng-container *ngIf="indirectCostOption === 'financial-accounting' || (costAccounting$ | async)?.state !== stateEnum.INITIALIZED ; else manual">
                            <app-cost-type-cost [selectedCostAccounting]="costAccounting$ | async"></app-cost-type-cost>
                        </ng-container>
                        <ng-template #manual>
                            <app-import-indirect-cost></app-import-indirect-cost>
                        </ng-template>
                    </div>
                    <div mat-dialog-actions>
                        <button mat-stroked-button mat-dialog-close (click)="close()">{{ "app.button.cancel.label" | translate }}</button>
                        <button mat-flat-button
                                color="primary"
                                [disabled]="(costAccounting$ | async)?.state !== stateEnum.INITIALIZED ||
                                (indirectCostOption === 'manual' && !importIndirectCostComponent?.importFormArray.valid) || state.isLoading"
                                (click)="startCostTypeAccounting()">
                            {{ "app.dialog.cost-accounting.cost-type-accounting.start.label" | translate }}
                        </button>
                    </div>
                </ng-template>
            </mat-step>
            <mat-step [stepControl]="stepValidationFormGroup.controls['costCenterAccountingStepControl']">
                <ng-template matStepLabel>{{ "app.dialog.cost-accounting.cost-center-accounting.label" | translate }}</ng-template>
                <ng-template matStepContent>
                    <div class="embeddedList">
                        <app-bab-rebooking [selectedCostAccounting]="costAccounting$ | async"></app-bab-rebooking>
                    </div>
                    <div mat-dialog-actions>
                        <button mat-stroked-button mat-dialog-close (click)="close()">{{ "app.button.cancel.label" | translate }}</button>
                        <button mat-flat-button
                                color="primary"
                                [disabled]="(costAccounting$ | async) === undefined || (costAccounting$ | async).state !== stateEnum.COST_TYPE_ACCOUNTING || state.isLoading"
                                (click)="continueCalculation()">
                            {{ "app.dialog.cost-accounting.cost-center-accounting.confirm.label" | translate }}
                        </button>
                    </div>
                </ng-template>
            </mat-step>
            <mat-step>
                <ng-template matStepContent>

                    <div class="info-text">
                        <mat-icon>info</mat-icon>
                        <p>{{ "app.dialog.cost-accounting.fallback-info.label" | translate }}
                        </p>
                    </div>

                    <mat-divider></mat-divider>

                    <div class="fallback-container" [formGroup]="fallbackOptionsFormGroup">
                        <div class="fallback-selection">
                            <mat-form-field appearance="fill">
                                <mat-label>{{ "app.dialog.cost-accounting.material-price.label" | translate }}</mat-label>
                                <mat-select [disabled]="true" [value]="(costAccounting$ | async)?.costAccountingType.materialPrice">
                                    <mat-option *ngFor="let materialPriceOption of materialPriceOptionEnum | keyvalue" [value]="materialPriceOption.key">
                                        {{ ("app.model.cost-accounting-type.materialPrice." + materialPriceOption.key + ".label") | translate }}
                                    </mat-option>
                                </mat-select>
                            </mat-form-field>
                            <mat-form-field appearance="fill">
                                <mat-label>{{ "app.dialog.cost-accounting.material-price-fallback.label" | translate }}</mat-label>
                                <mat-select formControlName="materialPrice">
                                    <ng-container *ngFor="let materialPriceOption of materialPriceOptionEnum | keyvalue">
                                        <mat-option
                                            *ngIf="materialPriceOption.key !== (costAccounting$ | async)?.costAccountingType.materialPrice"
                                            [value]="materialPriceOption.key">
                                            {{ ("app.model.cost-accounting-type.materialPrice." + materialPriceOption.key + ".label") | translate }}
                                        </mat-option>
                                    </ng-container>
                                    <mat-option [value]="'ZERO'">
                                        {{ "app.dialog.cost-accounting.material-price-fallback-ZERO.label" | translate }}
                                    </mat-option>
                                </mat-select>
                                <button [disabled]="(costAccounting$ | async)?.state === stateEnum.COST_UNIT_ACCOUNTING"
                                        matSuffix mat-icon-button (click)="fallbackOptionsFormGroup.controls.materialPrice.patchValue(null); $event.stopPropagation()">
                                    <mat-icon>clear</mat-icon>
                                </button>
                            </mat-form-field>
                        </div>

                        <div class="fallback-selection">
                            <mat-form-field appearance="fill">
                                <mat-label>{{ "app.dialog.cost-accounting.production-time.label" | translate }}</mat-label>
                                <mat-select [disabled]="true" [value]="(costAccounting$ | async)?.costAccountingType.productionTime">
                                    <mat-option *ngFor="let productionTimeOption of productionTimeOptionEnum | keyvalue" [value]="productionTimeOption.key">
                                        {{ ("app.model.cost-accounting-type.productionTime." + productionTimeOption.key + ".label") | translate }}
                                    </mat-option>
                                </mat-select>
                            </mat-form-field>

                            <mat-form-field appearance="fill">
                                <mat-label>{{ "app.dialog.cost-accounting.production-time-fallback.label" | translate }}</mat-label>
                                <mat-select formControlName="productionTime">
                                    <ng-container *ngFor="let productionTimeOption of productionTimeOptionEnum | keyvalue">
                                        <mat-option
                                            *ngIf="productionTimeOption.key !== (costAccounting$ | async)?.costAccountingType.productionTime"
                                            [value]="productionTimeOption.key">
                                            {{ ("app.model.cost-accounting-type.productionTime." + productionTimeOption.key + ".label") | translate }}
                                        </mat-option>
                                    </ng-container>
                                    <mat-option [value]="'ZERO'">
                                        {{ "app.dialog.cost-accounting.production-time-fallback-ZERO.label" | translate }}
                                    </mat-option>
                                </mat-select>
                                <button [disabled]="(costAccounting$ | async)?.state === stateEnum.COST_UNIT_ACCOUNTING"
                                        matSuffix mat-icon-button (click)="fallbackOptionsFormGroup.controls.productionTime.patchValue(null); $event.stopPropagation()">
                                    <mat-icon>clear</mat-icon>
                                </button>
                            </mat-form-field>
                        </div>

                        <div class="fallback-selection">
                            <mat-form-field appearance="fill">
                                <mat-label>{{ "app.dialog.cost-accounting.sales-price.label" | translate }}</mat-label>
                                <mat-select [disabled]="true" [value]="(costAccounting$ | async)?.costAccountingType.salesPrice">
                                    <mat-option *ngFor="let salesPriceOption of salesPriceOptionEnum | keyvalue" [value]="salesPriceOption.key">
                                        {{ ("app.model.cost-accounting-type.salesPrice." + salesPriceOption.key + ".label") | translate }}
                                    </mat-option>
                                </mat-select>
                            </mat-form-field>
                            <mat-form-field appearance="fill">
                                <mat-label>{{ "app.dialog.cost-accounting.sales-price-fallback.label" | translate }}</mat-label>
                                <mat-select formControlName="salesPrice">
                                    <ng-container *ngFor="let salesPriceOption of salesPriceOptionEnum | keyvalue">
                                        <mat-option
                                            *ngIf="salesPriceOption.key !== (costAccounting$ | async)?.costAccountingType.salesPrice"
                                            [value]="salesPriceOption.key">
                                            {{ ("app.model.cost-accounting-type.salesPrice." + salesPriceOption.key + ".label") | translate }}
                                        </mat-option>
                                    </ng-container>
                                    <mat-option [value]="'ZERO'">
                                        {{ "app.dialog.cost-accounting.sales-price-fallback-ZERO.label" | translate }}
                                    </mat-option>
                                </mat-select>
                                <button [disabled]="(costAccounting$ | async)?.state === stateEnum.COST_UNIT_ACCOUNTING"
                                        matSuffix mat-icon-button (click)="fallbackOptionsFormGroup.controls.salesPrice.patchValue(null); $event.stopPropagation()">
                                    <mat-icon>clear</mat-icon>
                                </button>
                            </mat-form-field>
                        </div>
                    </div>

                    <div mat-dialog-actions>
                        <button mat-stroked-button mat-dialog-close (click)="close()">{{ "app.button.cancel.label" | translate }}</button>
                        <button mat-flat-button
                                color="primary"
                                [disabled]="(costAccounting$ | async) === undefined || (costAccounting$ | async).state !== stateEnum.COST_CENTER_APPORTIONMENTS
                                            || state.isLoading"
                                (click)="continueCalculation()">
                            {{ "app.dialog.cost-accounting.cost-unit-accounting.confirm.label" | translate }}
                        </button>
                    </div>
                </ng-template>
                <ng-template matStepLabel>{{ "app.dialog.cost-accounting.cost-unit-accounting.label" | translate }}</ng-template>
            </mat-step>

        </mat-stepper>

    </div>
</ng-container>
